// H1 : documents (liste, fiche avec versions, dépôt, téléchargement par lien signé de courte durée).
// Charte D-038 (S03, M03) : recherche en tête, filtre par confidentialité, tableau avec étiquettes,
// action principale « Ajouter un document » seulement quand le droit de dépôt existe sur l'objet ciblé.

import { useRef, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NIVEAUX_CONFIDENTIALITE, TAILLE_MAX_DOCUMENT_OCTETS } from '@carburanttrack/shared';
import { api } from '../../api/client';
import type { DocumentListe, InstallationFiche, InstallationListe } from '../../api/types';
import { useSession } from '../../app/session';
import { Message, MessageErreur, Squelette } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { dateHeure, libelleConfidentialite } from '../../composants/format';

const TYPES_ACCEPTES = '.pdf,.jpg,.jpeg,.png,.xlsx,.csv';
const LIBELLES_OBJETS: Record<string, string> = { installation: 'Installation', organisation: 'Organisation' };

async function telecharger(id: string, version?: number) {
  const lien = await api<{ url: string }>(`/api/v1/documents/${id}/lien`, { corps: version ? { version } : {} });
  // Le lien signé expire après 5 minutes et reste lié à l'utilisateur ; les droits sont réévalués à l'accès.
  window.location.assign(lien.donnees.url);
}

function TableauDocuments({ documents }: { documents: DocumentListe[] }) {
  const [erreur, setErreur] = useState<unknown>(null);
  return (
    <>
      {erreur !== null && <MessageErreur erreur={erreur} titre="Téléchargement refusé" />}
      <div className="tableau-conteneur">
        <table className="tableau">
          <caption className="visuellement-cache">Documents</caption>
          <thead>
            <tr>
              <th scope="col">Titre</th>
              <th scope="col">Confidentialité</th>
              <th scope="col" className="masquable">
                Rattaché à
              </th>
              <th scope="col" className="nombre masquable">
                Version
              </th>
              <th scope="col" className="masquable">
                Déposé le
              </th>
              <th scope="col">
                <span className="visuellement-cache">Action</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {documents.map((d) => (
              <tr key={d.id}>
                <td>
                  <Link className="lien-ligne" to={`/documents/${d.id}`}>
                    {d.titre}
                  </Link>
                  {d.categorie && <small>{d.categorie}</small>}
                </td>
                <td>
                  <span className="etiquette">{libelleConfidentialite(d.confidentialite)}</span>
                </td>
                <td className="masquable">
                  {LIBELLES_OBJETS[d.objetType] ?? d.objetType}
                  <small>{d.organisationNom ?? 'sans organisation'}</small>
                </td>
                <td className="nombre masquable">{d.versionCourante}</td>
                <td className="masquable">{dateHeure(d.creeLe)}</td>
                <td className="nombre">
                  <button type="button" className="bouton" aria-label={`Télécharger ${d.titre}`} onClick={() => telecharger(d.id).catch(setErreur)}>
                    <Icone nom="document" />
                    <span className="libelle-bouton">Télécharger</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** Installation sur laquelle l'utilisateur peut déposer depuis la liste : celle de l'adresse, ou sa station. */
function useCibleDepot(objetId: string | null, objetType: string | null) {
  const { etat } = useSession();
  const station = useQuery({
    queryKey: ['installations', 'ma-station'],
    queryFn: async () => (await api<InstallationListe[]>('/api/v1/installations?limite=5')).donnees,
    enabled: !objetId && etat?.profil === 'station',
    retry: false,
  });
  const id = objetId && (objetType ?? 'installation') === 'installation' ? objetId : !objetId ? station.data?.[0]?.id : undefined;
  return useQuery({
    queryKey: ['installation', id],
    queryFn: async () => (await api<InstallationFiche>(`/api/v1/installations/${id}`)).donnees,
    enabled: Boolean(id),
    retry: false,
  });
}

export function ListeDocuments() {
  const [params] = useSearchParams();
  const objetId = params.get('objetId');
  const objetType = params.get('objetType');
  const [q, setQ] = useState('');
  const [filtre, setFiltre] = useState('');
  const [confidentialite, setConfidentialite] = useState('');
  const [ajout, setAjout] = useState(false);
  const cible = useCibleDepot(objetId, objetType);
  const requete = useQuery({
    queryKey: ['documents', objetId, filtre, confidentialite],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (objetId) p.set('objetId', objetId);
      if (filtre) p.set('q', filtre);
      if (confidentialite) p.set('confidentialite', confidentialite);
      return (await api<DocumentListe[]>(`/api/v1/documents?${p.toString()}`)).donnees;
    },
  });
  const fiche = cible.data;
  const peutDeposer = Boolean(fiche?.droits.deposerDocument);
  const filtresActifs = Boolean(filtre || confidentialite);

  return (
    <div className="page-documents">
      <div className="page-tete">
        <div>
          <h1>Documents</h1>
          <p>{fiche ? `Documents visibles pour ${fiche.nom} et l’organisation ${fiche.organisationNom}.` : 'Les documents que vos habilitations vous permettent de consulter.'}</p>
        </div>
        {peutDeposer && !ajout && (
          <button type="button" className="bouton primaire" onClick={() => setAjout(true)}>
            <Icone nom="ajout" />
            Ajouter un document
          </button>
        )}
      </div>

      {ajout && fiche && (
        <section className="carte-bloc" aria-labelledby="titre-ajout">
          <div className="carte-bloc-tete">
            <h2 id="titre-ajout">Ajouter un document</h2>
            <button type="button" className="bouton discret" onClick={() => setAjout(false)}>
              Fermer
            </button>
          </div>
          <p className="attenue">Rattaché à {fiche.nom} : le document en hérite les droits de lecture.</p>
          <FormulaireDepot objetType="installation" objetId={fiche.id} sansTitre />
        </section>
      )}

      <form
        className="barre-filtres"
        role="search"
        aria-label="Recherche de documents"
        onSubmit={(e) => {
          e.preventDefault();
          setFiltre(q.trim());
        }}
      >
        <div className="champ large">
          <label htmlFor="doc-q">Titre du document</label>
          <input id="doc-q" type="search" placeholder="Rechercher par titre" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <button type="submit" className="bouton primaire">
          Rechercher
        </button>
        <div className="puces filtres-puces" role="radiogroup" aria-label="Confidentialité">
          {['', ...NIVEAUX_CONFIDENTIALITE].map((n) => (
            <button key={n || 'tous'} type="button" role="radio" className="puce" aria-checked={confidentialite === n} onClick={() => setConfidentialite(n)}>
              {n ? libelleConfidentialite(n) : 'Toutes'}
            </button>
          ))}
        </div>
      </form>

      {requete.isLoading && <Squelette lignes={5} />}
      {requete.error && <MessageErreur erreur={requete.error} />}
      {requete.data && requete.data.length > 0 && <TableauDocuments documents={requete.data} />}
      {requete.data && requete.data.length === 0 && (
        <section className="etat-vide" aria-labelledby="titre-vide">
          <span className="pictogramme" aria-hidden="true">
            <Icone nom="document" taille={30} epaisseur={1.6} />
          </span>
          <h2 id="titre-vide">{filtresActifs ? 'Aucun document ne correspond à ces critères' : 'Aucun document visible pour l’instant'}</h2>
          <p>Pour déposer un document, ouvrez la fiche de l’installation ou de l’organisation concernée : le document en hérite les droits de lecture.</p>
          {fiche && (
            <div className="actions">
              {peutDeposer && (
                <button type="button" className="bouton" onClick={() => setAjout(true)}>
                  Ajouter un document à {fiche.nom}
                </button>
              )}
              <Link className="bouton" to={`/referentiel/installations/${fiche.id}`}>
                Fiche de {fiche.nom}
              </Link>
            </div>
          )}
        </section>
      )}
      {requete.data && requete.data.length > 0 && (
        <p className="note">
          <Icone nom="aide" />
          <span>Pour déposer un document, ouvrez la fiche de l’installation ou de l’organisation concernée : le document en hérite les droits de lecture.</span>
        </p>
      )}
    </div>
  );
}

export function DocumentsObjet({ objetType, objetId, peutDeposer }: { objetType: string; objetId: string; peutDeposer: boolean }) {
  const requete = useQuery({
    queryKey: ['documents', objetId],
    queryFn: async () => (await api<DocumentListe[]>(`/api/v1/documents?objetType=${objetType}&objetId=${objetId}`)).donnees,
    retry: false,
  });
  return (
    <div>
      {requete.isLoading && <Squelette lignes={2} />}
      {requete.error && <p className="attenue">Documents non accessibles avec vos habilitations.</p>}
      {requete.data && requete.data.length === 0 && <p className="attenue">Aucun document rattaché.</p>}
      {requete.data && requete.data.length > 0 && <TableauDocuments documents={requete.data} />}
      {peutDeposer && <FormulaireDepot objetType={objetType} objetId={objetId} />}
    </div>
  );
}

function FormulaireDepot({ objetType, objetId, documentId, sansTitre = false }: { objetType?: string; objetId?: string; documentId?: string; sansTitre?: boolean }) {
  const client = useQueryClient();
  const fichierRef = useRef<HTMLInputElement>(null);
  const [titre, setTitre] = useState('');
  const [confidentialite, setConfidentialite] = useState('restreinte');
  const [commentaire, setCommentaire] = useState('');
  const [fichier, setFichier] = useState<File | null>(null);
  const tropGros = fichier !== null && fichier.size > TAILLE_MAX_DOCUMENT_OCTETS;
  const envoi = useMutation({
    mutationFn: async () => {
      const f = new FormData();
      if (documentId) {
        f.append('commentaire', commentaire);
      } else {
        f.append('titre', titre);
        f.append('objetType', objetType!);
        f.append('objetId', objetId!);
        f.append('confidentialite', confidentialite);
      }
      f.append('fichier', fichier!);
      return api(documentId ? `/api/v1/documents/${documentId}/versions` : '/api/v1/documents', { formulaire: f });
    },
    onSuccess: async () => {
      setTitre('');
      setCommentaire('');
      setFichier(null);
      if (fichierRef.current) fichierRef.current.value = '';
      await client.invalidateQueries({ queryKey: ['documents'] });
      if (documentId) await client.invalidateQueries({ queryKey: ['document', documentId] });
    },
  });
  const id = documentId ?? objetId;
  return (
    <form
      className="formulaire-depot"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        envoi.mutate();
      }}
    >
      {!sansTitre && <h3>{documentId ? 'Déposer une nouvelle version' : 'Déposer un document'}</h3>}
      <div className="formulaire-ligne">
        {!documentId && (
          <>
            <div className="champ">
              <label htmlFor={`dep-titre-${id}`}>Titre</label>
              <input id={`dep-titre-${id}`} value={titre} onChange={(e) => setTitre(e.target.value)} />
            </div>
            <div className="champ">
              <label htmlFor={`dep-conf-${id}`}>Confidentialité</label>
              <select id={`dep-conf-${id}`} value={confidentialite} onChange={(e) => setConfidentialite(e.target.value)}>
                {NIVEAUX_CONFIDENTIALITE.map((n) => (
                  <option key={n} value={n}>
                    {libelleConfidentialite(n)}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
        {documentId && (
          <div className="champ">
            <label htmlFor={`dep-com-${id}`}>Commentaire</label>
            <input id={`dep-com-${id}`} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
          </div>
        )}
        <div className="champ">
          <label htmlFor={`dep-fichier-${id}`}>Fichier (PDF, JPEG, PNG, XLSX ou CSV ; 10 Mo au plus)</label>
          <input id={`dep-fichier-${id}`} ref={fichierRef} type="file" accept={TYPES_ACCEPTES} onChange={(e) => setFichier(e.target.files?.[0] ?? null)} />
          {tropGros && <span className="erreur-champ">Le fichier dépasse 10 Mo.</span>}
        </div>
      </div>
      {envoi.error && <MessageErreur erreur={envoi.error} titre="Dépôt refusé" />}
      {envoi.isSuccess && <Message type="succes">Document déposé et chiffré.</Message>}
      <button type="submit" className="bouton primaire" disabled={!fichier || tropGros || (!documentId && titre.trim().length === 0) || envoi.isPending}>
        {envoi.isPending ? 'Dépôt en cours' : 'Déposer'}
      </button>
    </form>
  );
}

interface DocumentDetaille extends DocumentListe {
  objetLibelle: string | null;
  versions: { id: string; numero: number; nomFichier: string; typeFichier: string; taille: number; empreinteSha256: string; commentaire: string | null; creeLe: string; auteurPrenom: string | null; auteurNom: string | null }[];
}

export function FicheDocument() {
  const { id } = useParams();
  const [erreur, setErreur] = useState<unknown>(null);
  const r = useQuery({ queryKey: ['document', id], queryFn: async () => (await api<DocumentDetaille>(`/api/v1/documents/${id}`)).donnees });
  if (r.isLoading) return <Squelette lignes={5} />;
  if (r.error) return <MessageErreur erreur={r.error} titre="Document introuvable ou non autorisé" />;
  const d = r.data!;
  const lienObjet = d.objetType === 'installation' ? `/referentiel/installations/${d.objetId}` : d.objetType === 'organisation' ? `/referentiel/organisations/${d.objetId}` : null;
  const derniere = d.versions[0];
  return (
    <div className="page-document">
      <div className="page-tete">
        <div>
          <span className="contexte">
            {LIBELLES_OBJETS[d.objetType] ?? d.objetType} : {lienObjet ? <Link to={lienObjet}>{d.objetLibelle ?? d.objetId}</Link> : d.objetLibelle}
          </span>
          <h1>{d.titre}</h1>
          <div className="actions">
            <span className="etiquette">{libelleConfidentialite(d.confidentialite)}</span>
            <span className="attenue">
              Version {d.versionCourante}, déposée le {dateHeure(d.creeLe)}
            </span>
          </div>
        </div>
        {derniere && (
          <button type="button" className="bouton primaire" onClick={() => telecharger(d.id, derniere.numero).catch(setErreur)}>
            <Icone nom="document" />
            Télécharger la version {derniere.numero}
          </button>
        )}
      </div>
      {erreur !== null && <MessageErreur erreur={erreur} titre="Téléchargement refusé" />}
      <section className="carte-bloc" aria-labelledby="titre-versions">
        <h2 id="titre-versions">Versions</h2>
        <div className="tableau-conteneur">
          <table className="tableau">
            <thead>
              <tr>
                <th scope="col" className="nombre">
                  Version
                </th>
                <th scope="col">Fichier</th>
                <th scope="col" className="nombre masquable">
                  Taille
                </th>
                <th scope="col" className="masquable">
                  Déposé par
                </th>
                <th scope="col" className="masquable">
                  Empreinte SHA-256
                </th>
                <th scope="col">
                  <span className="visuellement-cache">Action</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {d.versions.map((v) => (
                <tr key={v.id}>
                  <td className="nombre">{v.numero}</td>
                  <td>
                    {v.nomFichier}
                    {v.commentaire && <small>{v.commentaire}</small>}
                  </td>
                  <td className="nombre masquable">{Math.ceil(v.taille / 1024)} Ko</td>
                  <td className="masquable">
                    {v.auteurPrenom} {v.auteurNom}
                    <small>{dateHeure(v.creeLe)}</small>
                  </td>
                  <td className="masquable empreinte">{v.empreinteSha256.slice(0, 16)}</td>
                  <td className="nombre">
                    <button type="button" className="bouton" aria-label={`Télécharger la version ${v.numero}`} onClick={() => telecharger(d.id, v.numero).catch(setErreur)}>
                      <Icone nom="document" />
                      <span className="libelle-bouton">Télécharger</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="carte-bloc" aria-labelledby="titre-nouvelle-version">
        <h2 id="titre-nouvelle-version">Déposer une nouvelle version</h2>
        <FormulaireDepot documentId={d.id} sansTitre />
      </section>
    </div>
  );
}
